import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { accessApi } from "../../services/accessApi";

function Block({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-4 shadow-sm">
      <h2 className="text-base font-semibold text-black dark:text-white mb-3">
        {title}
      </h2>
      {children}
    </div>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    green:
      "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900",
    blue:
      "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900",
    gray:
      "bg-gray-50 text-gray-700 border-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:border-neutral-700",
    yellow:
      "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900",
    red:
      "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900",
  };

  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}
    >
      {children}
    </span>
  );
}

export default function ITUserAccessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    load();
  }, [id]);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await accessApi.getUserAccessDetail(id);
      setData(res?.data?.data || null);
    } catch (err) {
      console.error("ITUserAccessDetailPage error:", err);
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Failed to load access detail."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-black dark:text-white">Loading access detail...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600 dark:text-red-400">{error}</div>;
  }

  if (!data) {
    return <div className="p-6 text-black dark:text-white">No data.</div>;
  }

  const warnings = [];

  if (!data.person) {
    warnings.push({
      tone: "red",
      text: "User is not linked to an HR person.",
    });
  }

  if (data.person && !data.assignments?.length) {
    warnings.push({
      tone: "yellow",
      text: "Linked HR person has no active position assignments.",
    });
  }

  if (data.hr_roles?.length > 0 && !data.policies?.length) {
    warnings.push({
      tone: "yellow",
      text: "Roles exist, but no policies are mapped.",
    });
  }

  if (data.policies?.length > 0 && !data.permissions?.length) {
    warnings.push({
      tone: "yellow",
      text: "Policies exist, but no permissions are assigned.",
    });
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-white">
            Role → Policy Mapping
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Effective access for selected user.
          </p>
        </div>

        <button
          onClick={() => navigate("/it/access/users")}
          className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-black dark:text-white"
        >
          Back
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Block title="User">
          <div className="space-y-2 text-sm text-black dark:text-white">
            <div>
              <strong>ID:</strong> {data.user?.id || "-"}
            </div>
            <div>
              <strong>Name:</strong> {data.user?.display_name || "-"}
            </div>
            <div>
              <strong>Email:</strong> {data.user?.email || "-"}
            </div>
            <div>
              <strong>UID:</strong> {data.user?.uid || "-"}
            </div>
          </div>
        </Block>

        <Block title="Linked Person">
          <div className="space-y-2 text-sm text-black dark:text-white">
            <div>
              <strong>Person:</strong> {data.person?.full_name || "-"}
            </div>
            <div>
              <strong>Type:</strong> {data.person?.person_type || "-"}
            </div>
            <div>
              <strong>Status:</strong> {data.person?.status || "-"}
            </div>
            <div>
              <strong>Primary Position:</strong> {data.person?.position_title || "-"}
            </div>
            <div>
              <strong>Department:</strong> {data.person?.department_name || "-"}
            </div>
            <div>
              <strong>Team:</strong> {data.person?.team_name || "-"}
            </div>
          </div>
        </Block>

        <Block title="Warnings">
          <div className="space-y-2">
            {warnings.length > 0 ? (
              warnings.map((w, idx) => (
                <div key={idx}>
                  <Badge tone={w.tone}>{w.text}</Badge>
                </div>
              ))
            ) : (
              <span className="text-gray-500 dark:text-gray-400 text-sm">
                No access warnings detected.
              </span>
            )}
          </div>
        </Block>
      </div>

      <Block title="Active Positions">
        <div className="space-y-2 text-sm text-black dark:text-white">
          {(data.assignments || []).map((a, idx) => (
            <div key={`${a.position_id}-${idx}`}>
              <strong>{a.title || a.position_title || "-"}</strong>
              {a.code || a.position_code ? (
                <span className="text-gray-500 dark:text-gray-400">
                  {" "}
                  ({a.code || a.position_code})
                </span>
              ) : null}
            </div>
          ))}
          {!data.assignments?.length && (
            <span className="text-gray-500 dark:text-gray-400">
              No active positions.
            </span>
          )}
        </div>
      </Block>

      <Block title="Roles">
        <div className="flex flex-wrap gap-2">
          {(data.hr_roles || []).map((r, idx) => (
            <Badge key={`${r.role_key}-${r.source}-${idx}`} tone="blue">
              {r.role_key} · {r.source}
            </Badge>
          ))}
          {!data.hr_roles?.length && (
            <span className="text-gray-500 dark:text-gray-400">No roles.</span>
          )}
        </div>
      </Block>

      <Block title="Policies">
        <div className="space-y-2">
          {(data.policies || []).map((p) => (
            <div key={p.code} className="text-sm text-black dark:text-white">
              <strong>{p.code}</strong> — {p.name}{" "}
              <span className="text-gray-500 dark:text-gray-400">
                ({p.source})
              </span>
            </div>
          ))}
          {!data.policies?.length && (
            <span className="text-gray-500 dark:text-gray-400">No policies.</span>
          )}
        </div>
      </Block>

      <Block title="Permissions">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
          {(data.permissions || []).map((perm) => (
            <div
              key={perm.code}
              className="rounded-xl border border-gray-200 dark:border-neutral-700 px-3 py-2 text-sm text-black dark:text-white"
            >
              <div className="font-medium">{perm.code}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {perm.module_code}
              </div>
            </div>
          ))}
          {!data.permissions?.length && (
            <span className="text-gray-500 dark:text-gray-400">
              No permissions.
            </span>
          )}
        </div>
      </Block>

      <Block title="Scopes">
        <div className="space-y-2">
          {(data.scopes || []).map((s) => (
            <div
              key={`${s.module_code}-${s.scope}-${s.source_type}-${s.source_key}`}
              className="text-sm text-black dark:text-white"
            >
              <strong>{s.module_code}</strong> → {s.scope}
              <span className="text-gray-500 dark:text-gray-400">
                {" "}
                ({s.source_type}: {s.source_key})
              </span>
            </div>
          ))}
          {!data.scopes?.length && (
            <span className="text-gray-500 dark:text-gray-400">No scopes.</span>
          )}
        </div>
      </Block>

      <Block title="Visible Modules">
        <div className="flex flex-wrap gap-2">
          {(data.visible_modules || []).map((m) => (
            <Badge key={m}>{m}</Badge>
          ))}
          {!data.visible_modules?.length && (
            <span className="text-gray-500 dark:text-gray-400">
              No visible modules.
            </span>
          )}
        </div>
      </Block>

      <Block title="Explain Access">
        <div className="space-y-6 text-sm text-black dark:text-white">
          <div>
            <div className="font-semibold mb-2">Position → Role</div>
            <div className="space-y-1">
              {(data.explain?.role_sources || []).map((r, idx) => (
                <div key={`${r.position_id}-${r.role_key}-${idx}`}>
                  <strong>{r.position_title}</strong>
                  <span className="text-gray-500 dark:text-gray-400"> → </span>
                  <span>{r.role_key}</span>
                </div>
              ))}
              {!data.explain?.role_sources?.length && (
                <div className="text-gray-500 dark:text-gray-400">
                  No role sources.
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="font-semibold mb-2">Role → Policy</div>
            <div className="space-y-1">
              {(data.explain?.policy_sources || []).map((p, idx) => (
                <div key={`${p.hr_role_key}-${p.policy_code}-${idx}`}>
                  <strong>{p.hr_role_key}</strong>
                  <span className="text-gray-500 dark:text-gray-400"> → </span>
                  <span>{p.policy_code}</span>
                </div>
              ))}
              {!data.explain?.policy_sources?.length && (
                <div className="text-gray-500 dark:text-gray-400">
                  No policy sources.
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="font-semibold mb-2">Policy → Permission</div>
            <div className="space-y-1">
              {(data.explain?.permission_sources || []).map((p, idx) => (
                <div key={`${p.policy_code}-${p.permission_code}-${idx}`}>
                  <strong>{p.policy_code}</strong>
                  <span className="text-gray-500 dark:text-gray-400"> → </span>
                  <span>{p.permission_code}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {" "}
                    ({p.module_code})
                  </span>
                </div>
              ))}
              {!data.explain?.permission_sources?.length && (
                <div className="text-gray-500 dark:text-gray-400">
                  No permission sources.
                </div>
              )}
            </div>
          </div>
        </div>
      </Block>
    </div>
  );
}